// pool/shell.jsx — the Pool page: a full takeover from the menu. Built from
// the canvas Ben approved on 2026-10-26.
//
// Water (the settled temperature, when it was taken, the week, the pump's
// schedule), the cleaner with Start and Stop, the underwater light and the
// heater. Two columns on the iPad, one on a phone.
//
// Stop runs in HA (ha-pool `script.pool_cleaner_family_stop`) so a page
// closed halfway cannot strand the plug off; this page only reads its
// steps back. Colour: the pool's readings are pool blue; rust only where
// someone has to do something (lift it out, check the pool).

import * as React from 'react';
import { TakeoverShell, fs, BusyDot } from '../direction-a.jsx';
import { FONT, RADIUS } from '../theme.js';
import { Degrees } from '../degrees.jsx';
import {
  readPool, summary, measuredWhen, cleanedWhen, nextWhen, trendWords,
  dailyTemps, fmtClock, round5, CLEANER, LIGHT, PUMP, WASTE_MINUTES, fmtCountdown, scheduleLine,
} from './model.js';
import { usePoolTemps } from './use-pool.jsx';

const BUSY_FLOOR_MS = 1200;   // an instant settle reads as a press that did nothing

function useWidth() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const eyebrow = (T) => ({ fontSize: fs(T, 12), letterSpacing: '.12em', textTransform: 'uppercase', color: T.inkSoft, fontWeight: 500 });
const label = (T) => ({ fontSize: fs(T, 13), color: T.inkSoft });
const serif = (T, size, extra) => ({ fontFamily: FONT.serif, fontWeight: 500, fontSize: fs(T, size), lineHeight: 0.95, fontVariantNumeric: 'lining-nums', ...extra });
const body = (T) => ({ margin: 0, fontSize: fs(T, 14), lineHeight: 1.45, color: T.inkSoft });

function Panel({ T, children, style }) {
  return (
    <section style={{
      background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow, color: T.ink,
      padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, ...style,
    }}>{children}</section>
  );
}

function Dot({ color, size = 8 }) {
  return <span aria-hidden style={{ display: 'block', width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0 }}/>;
}

function Fact({ T, k, v, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={label(T)}>{k}</span>
      <span style={{ fontSize: fs(T, 16), fontWeight: 500, color: color || T.ink }}>{v}</span>
    </div>
  );
}

function BigButton({ T, primary, busy, disabled, onClick, icon, children, label: aria }) {
  const fill = primary && !busy ? T.primary : T.cardSoft;
  const ink = primary && !busy ? T.primaryInk : T.ink;
  return (
    <button onClick={onClick} disabled={disabled || busy} aria-busy={busy || undefined} aria-label={aria} style={{
      height: 52, border: 'none', borderRadius: RADIUS.control, background: fill, color: ink,
      fontFamily: FONT.sans, fontSize: fs(T, 16), fontWeight: 600, cursor: disabled || busy ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%',
      opacity: disabled && !busy ? 0.45 : 1,
    }}>
      {busy ? <BusyDot T={T} size={18}/> : icon}
      {children}
    </button>
  );
}

const PLAY = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M8 5l11 7-11 7z"/></svg>;
const STOP = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><rect x="6" y="6" width="12" height="12" rx="2"/></svg>;

function Toggle({ T, on, disabled, busy, onClick, label: aria }) {
  return (
    <button onClick={onClick} disabled={disabled || busy} aria-pressed={on} aria-busy={busy || undefined} aria-label={aria} style={{
      width: 64, height: 36, border: 'none', borderRadius: RADIUS.full, padding: 3, flexShrink: 0,
      background: on ? T.litFill : T.cardSoft, display: 'flex', alignItems: 'center',
      justifyContent: on ? 'flex-end' : 'flex-start', opacity: disabled ? 0.55 : 1,
      cursor: disabled || busy ? 'default' : 'pointer',
    }}>
      <span aria-hidden style={{
        width: 30, height: 30, borderRadius: '50%', background: T.keyFill, boxShadow: T.keyShadow,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{busy && <BusyDot T={T} size={14}/>}</span>
    </button>
  );
}

// ── Water ──────────────────────────────────────────────────────────

function TempChart({ T, days }) {
  const [ref, width] = useWidth();
  const h = 112, top = 22, bottom = 92;
  const ok = width > 0 && days && days.length >= 2;
  let content = null;
  if (ok) {
    const vals = days.map((d) => d.mean);
    const lo = Math.floor(Math.min(...vals) - 0.5), hi = Math.ceil(Math.max(...vals) + 0.5);
    const x = (i) => 4 + (i / (days.length - 1)) * (width - 8);
    const y = (v) => bottom - ((v - lo) / Math.max(1, hi - lo)) * (bottom - top);
    const pts = days.map((d, i) => `${x(i).toFixed(1)},${y(d.mean).toFixed(1)}`).join(' ');
    const first = days[0], last = days[days.length - 1];
    const dayName = (d) => d.day.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    content = (
      <svg width="100%" viewBox={`0 0 ${width} ${h + 18}`} role="img" style={{ display: 'block', overflow: 'visible' }}
        aria-label={`Daily water temperature, ${Math.round(first.mean)} degrees on ${dayName(first)} to ${Math.round(last.mean)} degrees today`}>
        <line x1={0} x2={width} y1={bottom + 4} y2={bottom + 4} stroke={T.hairHi} strokeWidth={1}/>
        <polyline points={pts} fill="none" stroke={T.pool} strokeWidth={1.9} strokeLinejoin="round" strokeLinecap="round"/>
        <circle cx={x(0)} cy={y(first.mean)} r={3} fill={T.pool}/>
        <circle cx={x(days.length - 1)} cy={y(last.mean)} r={4} fill={T.pool}/>
        <g fontSize={fs(T, 12)} fontFamily={FONT.sans} fontWeight={600} fill={T.pool}>
          <text x={x(0) + 6} y={y(first.mean) - 8}>{Math.round(first.mean)}°</text>
          <text x={x(days.length - 1) - 4} y={y(last.mean) - 10} textAnchor="end">{Math.round(last.mean)}°</text>
        </g>
        <g fontSize={fs(T, 12)} fontFamily={FONT.sans} fill={T.inkSoft}>
          <text x={0} y={h + 14}>{dayName(first)}</text>
          <text x={width} y={h + 14} textAnchor="end">Today</text>
        </g>
      </svg>
    );
  }
  // overflow hidden so a wider svg cannot hold the box open and feed its
  // own width back through the observer.
  return <div ref={ref} style={{ width: '100%', minWidth: 0, overflow: 'hidden', minHeight: ok ? 0 : 20 }}>{content}</div>;
}

function WaterCard({ T, water, temps, now }) {
  const days = temps ? dailyTemps(temps, water, now) : null;
  const trend = trendWords(days);
  const at = measuredWhen(water.at, now);
  return (
    <Panel T={T}>
      <span style={eyebrow(T)}>Water</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        {water.temp != null
          ? <Degrees T={T} value={water.temp} size={fs(T, 84)} color={T.pool}/>
          : <span style={serif(T, 84, { color: T.inkSoft })}>–</span>}
        {trend && <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>{trend}</span>}
      </div>
      <p style={body(T)}>
        {at ? `Measured ${at}. ` : ''}The probe only reads the pool while water is flowing through the pump.
      </p>
      <TempChart T={T} days={days}/>
    </Panel>
  );
}

// ── Cleaner ────────────────────────────────────────────────────────

const STEP = {
  asking: 'Asking it to stop. Waiting for it to stop drawing power.',
  cutting: "It didn't stop when asked, so its power is being switched off.",
  checking: 'Power is back on. Checking it stays stopped.',
};

function CleanerCard({ T, c, now, call }) {
  const [pressed, setPressed] = React.useState(null);      // { kind, at } for the busy floor
  React.useEffect(() => {
    if (!pressed) return undefined;
    const id = setTimeout(() => setPressed(null), BUSY_FLOOR_MS);
    return () => clearTimeout(id);
  }, [pressed]);

  const stopping = ['asking', 'cutting', 'checking'].includes(c.phase) || pressed?.kind === 'stop';
  const starting = c.phase === 'starting' || pressed?.kind === 'start';
  const run = (kind, entity) => { setPressed({ kind, at: Date.now() }); call('script', 'turn_on', { entity_id: entity }); };

  let title, color = T.ink, lines = [], action;
  if (stopping) {
    title = 'Stopping';
    lines = [<p key="s" style={body(T)}>{STEP[c.phase] || STEP.asking}</p>];
    action = <BigButton T={T} busy>{c.phase === 'cutting' ? 'Switching off…' : 'Stopping…'}</BigButton>;
  } else if (starting) {
    title = 'Starting';
    lines = [<p key="s" style={body(T)}>It can take a couple of minutes to get going.</p>];
    action = <BigButton T={T} busy>Starting…</BigButton>;
  } else if (c.phase === 'cleaning') {
    title = 'Cleaning';
    lines = [<p key="s" style={{ margin: 0, fontSize: fs(T, 15), color: T.inkSoft }}>
      {c.lastStart ? `Started ${fmtClock(round5(c.lastStart))}` : 'Running'}{c.doneBy ? `, done about ${fmtClock(c.doneBy)}` : ''}
    </p>];
    action = <BigButton T={T} primary icon={STOP} onClick={() => run('stop', CLEANER.stop)}>Stop</BigButton>;
  } else if (c.phase === 'stopped' || c.phase === 'powerOff') {
    title = 'Stopped';
    lines = [
      <span key="r" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: fs(T, 15), fontWeight: 600, lineHeight: 1.4, color: T.attention }}>
        <span style={{ marginTop: 7 }}><Dot color={T.attention}/></span>It may still be in the pool. Lift it out by its cable before you swim.
      </span>,
      c.phase === 'powerOff' && <p key="p" style={body(T)}>Its power is off, so it won't clean on schedule until someone presses Start.</p>,
    ];
    action = <BigButton T={T} icon={PLAY} onClick={() => run('start', CLEANER.start)}>Start a clean</BigButton>;
  } else if (c.phase === 'unconfirmed') {
    title = "Can't confirm it stopped";
    color = T.attention;
    lines = [<p key="u" style={{ margin: 0, fontSize: fs(T, 15), lineHeight: 1.4 }}>Its power monitor isn't reporting. Check the pool before anyone gets in.</p>];
    action = <BigButton T={T} primary icon={STOP} onClick={() => run('stop', CLEANER.stop)}>Try Stop again</BigButton>;
  } else {
    title = 'Not cleaning';
    lines = [
      c.phase === 'notStarted' && (
        <span key="n" style={{ fontSize: fs(T, 15), fontWeight: 600, color: T.attention }}>It didn't start. Try again in a few minutes.</span>
      ),
      <div key="f" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
        <Fact T={T} k="Last cleaned" v={cleanedWhen(c.lastStart, c.lastEnd, now) || 'Not known'}/>
        <Fact T={T} k="Next clean" v={nextWhen(c.next, now) || 'None scheduled'}/>
      </div>,
      <p key="s" style={body(T)}>{c.schedule ? `${c.schedule} ` : ''}If it's still in the water, lift it out by its cable before you swim.</p>,
    ];
    action = <BigButton T={T} primary icon={PLAY} onClick={() => run('start', CLEANER.start)}>Start a clean</BigButton>;
  }

  return (
    <Panel T={T}>
      <span style={eyebrow(T)}>Cleaner</span>
      <span style={serif(T, 44, { color })}>{title}</span>
      {lines}
      <div style={{ flexGrow: 1 }}/>
      {action}
      {c.phase === 'idle' && <span style={{ ...label(T), textAlign: 'center' }}>Runs for {Math.round(c.minutes / 60) === c.minutes / 60 ? `${c.minutes / 60} hours` : `${c.minutes} minutes`}. Stop it here any time.</span>}
    </Panel>
  );
}

// ── Pump: Pause and Run-to-waste ───────────────────────────────────
//
// The sequence lives in ha-pool (pool_family_pump_controls.yaml); this card
// reads its phase back and offers the next step. Every step that has someone
// turn the filter handle tells them to wait until the pump is quiet: HA cannot
// tell whether it has stopped (no meter yet), the person at the pump can.

function Minutes({ T, value, onPick }) {
  return (
    <div role="group" aria-label="Minutes" style={{ display: 'grid', gridTemplateColumns: `repeat(${WASTE_MINUTES.length}, minmax(0, 1fr))`, gap: 8 }}>
      {WASTE_MINUTES.map((m) => (
        <button key={m} aria-pressed={m === value} onClick={() => onPick(m)} style={{
          height: 52, border: 'none', borderRadius: RADIUS.key + 2, fontFamily: FONT.sans, fontSize: fs(T, 16), fontWeight: 600,
          background: m === value ? T.selected : T.cardSoft, color: m === value ? T.selectedInk : T.ink, cursor: 'pointer',
        }}>{m} min</button>
      ))}
    </div>
  );
}

function PumpCard({ T, pump, now, call }) {
  const [pressed, setPressed] = React.useState(null);
  const [minutes, setMinutes] = React.useState(10);
  const [again, setAgain] = React.useState(false);
  React.useEffect(() => {
    if (!pressed) return undefined;
    const id = setTimeout(() => setPressed(null), BUSY_FLOOR_MS);
    return () => clearTimeout(id);
  }, [pressed]);
  React.useEffect(() => { if (pump.phase !== 'done') setAgain(false); }, [pump.phase]);

  const go = (key, entity, variables) => {
    setPressed(key);
    call('script', 'turn_on', variables ? { entity_id: entity, variables } : { entity_id: entity });
  };
  const busy = (key) => pressed === key;
  const quiet = (text) => <p style={{ margin: 0, fontSize: fs(T, 15), lineHeight: 1.45, color: T.ink }}>{text}</p>;
  const note = (text) => <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>{text}</span>;
  const rust = (text) => (
    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: fs(T, 15), fontWeight: 600, lineHeight: 1.4, color: T.attention }}>
      <span style={{ marginTop: 7 }}><Dot color={T.attention}/></span>{text}
    </span>
  );
  const offAt = pump.offSince ? fmtClock(pump.offSince) : null;
  const confirmBtn = (label) => (
    <BigButton T={T} primary busy={busy('resume')} onClick={() => go('resume', PUMP.resume)}>{label}</BigButton>
  );
  const chooser = (
    <>
      <Minutes T={T} value={minutes} onPick={setMinutes}/>
      {note('15 minutes at most. It stops by itself at the end.')}
    </>
  );
  const startBtn = (
    <BigButton T={T} primary busy={busy('run')} onClick={() => go('run', PUMP.run, { minutes })}>Start, {minutes} minutes</BigButton>
  );

  let title, color = T.ink, body = [], actions = [];
  const p = pump.phase;
  if (p === 'running') {
    title = 'Running to waste';
    body = [
      <div key="c" style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={serif(T, 56, { lineHeight: 0.9, fontVariantNumeric: 'lining-nums tabular-nums' })}>{fmtCountdown(pump.remainingMs)}</span>
        <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>left{pump.finishes ? `, stops at ${fmtClock(pump.finishes)}` : ''}</span>
      </div>,
      pump.last?.minutes && (
        <div key="b" style={{ height: 6, borderRadius: 3, background: T.cardSoft }}>
          <div style={{ width: `${Math.min(100, 100 * (1 - pump.remainingMs / (pump.last.minutes * 60000)))}%`, height: 6, borderRadius: 3, background: T.ink }}/>
        </div>
      ),
      note("If the pump hasn't started within a minute, press Stop now."),
    ];
    actions = [<BigButton key="s" T={T} primary busy={busy('finish')} onClick={() => go('finish', PUMP.finish)}>Stop now</BigButton>];
  } else if (pump.unconfirmed) {
    title = `Off since ${offAt}`;
    color = T.attention;
    body = [
      <p key="t" style={{ margin: 0, fontSize: fs(T, 15), lineHeight: 1.4 }}>The pool isn't being filtered. Check the handle is on Filter, then confirm.</p>,
      pump.told && note(`Parents' phones told at ${fmtClock(pump.told)}.`),
    ];
    actions = [confirmBtn("It's back on Filter")];
  } else if (p === 'stopping') {
    title = 'Stopping';
    body = [
      quiet('Wait until the pump is quiet. Then turn the handle on top of the filter to Waste.'),
      note('Never turn the handle while the pump is running.'),
    ];
    actions = [
      <BigButton key="w" T={T} primary busy={busy('onWaste')} onClick={() => go('onWaste', PUMP.onWaste)}>The handle is on Waste</BigButton>,
      <button key="c" onClick={() => go('resume', PUMP.resume)} style={{ height: 44, border: 'none', borderRadius: RADIUS.control, background: 'transparent', color: T.inkSoft, fontFamily: FONT.sans, fontSize: fs(T, 14), fontWeight: 500, cursor: 'pointer' }}>Cancel, the handle is still on Filter</button>,
    ];
  } else if (p === 'ready') {
    title = 'Run to waste for';
    body = [chooser];
    actions = [startBtn];
  } else if (p === 'done') {
    title = 'Stopped';
    body = again ? [chooser] : [
      rust("Wait until it's quiet, then turn the handle back to Filter."),
      note('The pump stays off until you confirm.'),
    ];
    actions = again ? [startBtn] : [
      confirmBtn("It's back on Filter"),
      <BigButton key="a" T={T} onClick={() => setAgain(true)}>Run to waste again</BigButton>,
    ];
  } else if (p === 'paused') {
    title = 'Paused';
    body = [<span key="p" style={{ fontSize: fs(T, 15), color: T.inkSoft }}>{offAt ? `Paused at ${offAt}. ` : ''}It stays off until you resume.</span>];
    actions = [confirmBtn('Resume, the handle is on Filter')];
  } else {
    title = pump.window ? 'On schedule' : 'No run scheduled';
    const lastToday = pump.last && pump.last.at.toDateString() === now.toDateString();
    body = [
      <span key="w" style={{ fontSize: fs(T, 15), color: T.inkSoft }}>{scheduleLine(pump.window, now)}</span>,
      lastToday && note(`Ran to waste for ${pump.last.minutes} minutes at ${fmtClock(pump.last.at)}.`),
    ];
    actions = [
      <BigButton key="r" T={T} primary busy={busy('stopping')} onClick={() => go('stopping', PUMP.stop, { phase: 'stopping' })}>Run to waste</BigButton>,
      <BigButton key="p" T={T} busy={busy('paused')} onClick={() => go('paused', PUMP.stop, { phase: 'paused' })}>Pause the pump</BigButton>,
    ];
  }

  return (
    <Panel T={T}>
      <span style={eyebrow(T)}>Pump</span>
      <span style={serif(T, 36, { color })}>{title}</span>
      {body}
      <div style={{ flexGrow: 1 }}/>
      {actions}
    </Panel>
  );
}

// ── Light and heater ───────────────────────────────────────────────

function LightCard({ T, light, call }) {
  const [busy, setBusy] = React.useState(false);
  const was = React.useRef(light.on);
  React.useEffect(() => {
    if (!busy) { was.current = light.on; return undefined; }
    const floor = setTimeout(() => { if (light.on !== was.current) setBusy(false); }, BUSY_FLOOR_MS);
    const cap = setTimeout(() => setBusy(false), 10000);
    return () => { clearTimeout(floor); clearTimeout(cap); };
  }, [busy, light.on]);
  return (
    <Panel T={T}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={eyebrow(T)}>Underwater light</span>
          <span style={serif(T, 34, { lineHeight: 1, color: light.on ? T.lit : light.connected ? T.ink : T.mute })}>{light.on ? 'On' : 'Off'}</span>
        </div>
        <Toggle T={T} on={light.on} busy={busy} disabled={!light.connected}
          label={light.connected ? `Underwater light, ${light.on ? 'on' : 'off'}` : 'Underwater light, not connected yet'}
          onClick={() => { setBusy(true); call('light', 'toggle', { entity_id: LIGHT }); }}/>
      </div>
      {!light.connected && <p style={body(T)}>Not connected yet. The switch works here as soon as it's repaired.</p>}
    </Panel>
  );
}

function HeaterCard({ T, heater, water }) {
  const step = (aria, path) => (
    <button disabled aria-label={aria} style={{
      width: 44, height: 44, border: 'none', borderRadius: RADIUS.full, background: T.cardSoft, color: T.ink,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden><path d={path}/></svg>
    </button>
  );
  return (
    <Panel T={T}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={eyebrow(T)}>Heater</span>
          <span style={serif(T, 34, { lineHeight: 1, color: heater.connected ? T.ink : T.mute })}>{heater.on ? 'On' : 'Off'}</span>
        </div>
        <Toggle T={T} on={heater.on} disabled={!heater.connected} label="Heater, not connected yet"/>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <Fact T={T} k="Water now" v={water.temp != null ? `${Math.round(water.temp)}°` : '–'} color={T.pool}/>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: heater.targetConnected ? 1 : 0.45 }}>
          <span style={label(T)}>Heat to</span>
          {step('Lower target temperature', 'M6 12h12')}
          <span style={{ minWidth: 48, textAlign: 'center' }}>
            {heater.target != null
              ? <Degrees T={T} value={heater.target} size={fs(T, 32)}/>
              : <span style={serif(T, 32)}>––</span>}
          </span>
          {step('Raise target temperature', 'M6 12h12M12 6v12')}
        </div>
      </div>
      {!heater.connected && (
        <p style={body(T)}>Not connected yet. On and off work here once it's wired in; the target temperature once iAquaLink can set it.</p>
      )}
    </Panel>
  );
}

// ── The page ───────────────────────────────────────────────────────

export function PoolShell({ open, ...props }) {
  if (!open) return null;
  return <PoolPage {...props}/>;
}

function PoolPage({ T, onClose, viewport, hass }) {
  const phone = viewport === 'phone';
  // Its own clock: the Stop steps and the busy floors move by the second.
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const pool = readPool(hass?.states, now);
  const temps = usePoolTemps(hass, true);
  const line = summary(pool, now);
  const call = (domain, service, data) => hass?.callService?.(domain, service, data);

  return (
    <TakeoverShell T={T} viewport={viewport} eyebrow="Wallace St" title="Pool" onClose={onClose} bodyless>
      <div style={{
        padding: phone ? '8px 18px calc(32px + env(safe-area-inset-bottom))' : '12px 36px calc(40px + env(safe-area-inset-bottom))',
        maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: phone ? 16 : 20,
        fontFamily: FONT.sans, color: T.ink,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px 18px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: fs(T, 17), fontWeight: 600, color: T.pool }}>{line.head}</span>
          {line.sub && <span style={{ fontSize: fs(T, 15), color: T.inkSoft }}>{line.sub}</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: phone ? 16 : 20, alignItems: 'stretch' }}>
          <WaterCard T={T} water={pool.water} temps={temps} now={now}/>
          <CleanerCard T={T} c={pool.cleaner} now={now} call={call}/>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))', gap: phone ? 16 : 20, alignItems: 'stretch' }}>
          <PumpCard T={T} pump={pool.pump} now={now} call={call}/>
          <LightCard T={T} light={pool.light} call={call}/>
          <HeaterCard T={T} heater={pool.heater} water={pool.water}/>
        </div>
      </div>
    </TakeoverShell>
  );
}
